import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Github, ArrowLeft } from 'lucide-react';
import PageLayout from '../layout/PageLayout';

const NotFoundPage: React.FC = () => {
  const messages = [
    'This page went on a training arc',
    'It\'ll be back eventually. Probably.',
    'This page is in another castle',
    'Error 404: Page not found (unlike your waifu)',
    'This URL doesn\'t exist in this timeline',
  ];

  const randomMessage = messages[Math.floor(Math.random() * messages.length)];

  return (
    <PageLayout>
      <section className="min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Giant 404 */}
            <h1 className="text-9xl md:text-[12rem] font-theme font-bold mb-4 text-gradient leading-none">
              404
            </h1>

            {/* Random message */}
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-4" style={{ color: 'var(--color-text)' }}>
              {randomMessage}
            </h2>

            {/* Weeb humor */}
            <div className="space-y-4 mb-8">
              <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
                You found a page that doesn't exist.
              </p>
              <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
                That's more than we can say for season 2 of{' '}
                <span className="italic">[insert anime here]</span>.
              </p>
            </div>

            {/* Navigation options */}
            <div
              className="p-6 rounded-xl mb-8"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <p className="mb-4 font-semibold" style={{ color: 'var(--color-primary)' }}>
                Meanwhile, check out:
              </p>
              <div className="space-y-2">
                <Link
                  to="/"
                  className="block hover:underline"
                  style={{ color: 'var(--color-text)' }}
                >
                  → Home
                </Link>
                <Link
                  to="/projects"
                  className="block hover:underline"
                  style={{ color: 'var(--color-text)' }}
                >
                  → Our Projects
                </Link>
                <a
                  href="https://github.com/theweebcoders"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:underline"
                  style={{ color: 'var(--color-text)' }}
                >
                  → Our GitHub
                </a>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/"
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  background: 'var(--gradient-primary)',
                  color: 'var(--color-bg)'
                }}
              >
                <Home size={20} />
                <span>Go Home</span>
              </Link>

              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                  backgroundColor: 'var(--color-primary)' + '10'
                }}
              >
                <ArrowLeft size={20} />
                <span>Go Back</span>
              </button>

              <a
                href="https://github.com/theweebcoders"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                  backgroundColor: 'var(--color-primary)' + '10'
                }}
              >
                <Github size={20} />
                <span>GitHub</span>
              </a>
            </div>

            {/* Fun fact */}
            <motion.div
              className="mt-12 p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--color-primary)' + '10',
                borderColor: 'var(--color-primary)' + '30',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <p className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                Fun fact:
              </p>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                This 404 page has more personality than most entire websites. You're welcome.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default NotFoundPage;
